import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { MobileContainer } from '@/components/layout/MobileContainer';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { MODULES } from '@/data/moduleData';
import { useLocalized, useT } from '@/i18n';
import { useGameStore } from '@/store/useGameStore';
import { motion } from 'framer-motion';
import { Calendar, Target, TrendingUp, Trophy } from 'lucide-react';

export default function ProgressScreen() {
  const { moduleProgress } = useGameStore();
  const t = useT();
  const modules = useLocalized(MODULES);

  const completedModules = Object.values(moduleProgress).filter(m => m.status === 'completed').length;
  const totalModules = MODULES.length;
  const overallPercent = (completedModules / totalModules) * 100;

  // Flatten all attempts and sort by date
  const allAttempts = Object.entries(moduleProgress)
    .flatMap(([modId, prog]) =>
      (prog.attempts || []).map(attempt => ({
        ...attempt,
        moduleId: modId,
        moduleTitle: modules.find(m => m.id === modId)?.title || t('ui.progress.unknownModule')
      }))
    )
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const totalScore = allAttempts.reduce((acc, curr) => {
    const attemptScore = Object.values(curr.scores).reduce((a, b) => a + b, 0);
    return acc + attemptScore;
  }, 0);

  return (
    <MobileContainer noPadding>
      <div className="flex-1 flex flex-col overflow-y-auto scrollbar-hide">
        {/* Header */}
        <div className="px-5 pt-6 pb-4">
          <motion.h1
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl font-extrabold font-heading text-foreground mb-1"
          >
            {t('ui.progress.title')}
          </motion.h1>
          <p className="text-sm text-muted-foreground font-body">
            {t('ui.progress.subtitle')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="px-5 grid grid-cols-2 gap-3 mb-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-4 flex flex-col gap-2 bg-primary/5 border-primary/10">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Target size={18} className="text-primary" />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground font-body uppercase tracking-wider">{t('ui.progress.completed')}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-bold font-heading">{completedModules}</span>
                  <span className="text-xs text-muted-foreground font-body">/ {totalModules}</span>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="p-4 flex flex-col gap-2 bg-secondary/5 border-secondary/10">
              <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center">
                <Trophy size={18} className="text-secondary" />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground font-body uppercase tracking-wider">{t('ui.progress.totalPoints')}</p>
                <span className="text-xl font-bold font-heading">{totalScore}</span>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Overall Progress */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="px-5 mb-6"
        >
          <Card className="p-5 shadow-soft border-border relative overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-primary" />
                <span className="text-sm font-bold font-heading text-foreground">{t('ui.progress.overall')}</span>
              </div>
              <span className="text-sm font-bold font-heading text-primary">{Math.round(overallPercent)}%</span>
            </div>
            <Progress value={overallPercent} className="h-2.5 bg-muted" />
          </Card>
        </motion.div>

        {/* History List */}
        <div className="px-5 pb-8 flex flex-col gap-3">
          <h2 className="text-sm font-bold font-heading text-foreground flex items-center gap-2">
            <Calendar size={16} />
            {t('ui.progress.history')}
          </h2>

          <div className="flex flex-col gap-2.5">
            {allAttempts.length === 0 ? (
              <div className="text-center py-12 px-4 border-2 border-dashed border-border rounded-2xl">
                <p className="text-sm text-muted-foreground font-body">{t('ui.progress.empty')}</p>
              </div>
            ) : (
              allAttempts.map((attempt, index) => {
                const attemptScore = Object.values(attempt.scores).reduce((a, b) => a + b, 0);
                return (
                  <motion.div
                    key={`${attempt.moduleId}-${index}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.05 }}
                  >
                    <Card className="p-3.5 flex items-center justify-between shadow-soft hover:border-primary/20 transition-colors">
                      <div className="min-w-0 flex-1 pr-3">
                        <h3 className="text-xs font-bold font-heading text-foreground truncate">{attempt.moduleTitle}</h3>
                        <p className="text-[10px] text-muted-foreground font-body">
                          {new Date(attempt.date).toLocaleDateString('ru-RU')} • {Math.floor(attempt.timeSeconds / 60)}:{(attempt.timeSeconds % 60).toString().padStart(2, '0')}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-block mb-1 ${attempt.errors === 0 ? 'bg-success/10 text-success' : attempt.errors <= 2 ? 'bg-warning/10 text-warning' : 'bg-destructive/10 text-destructive'
                          }`}>
                          {attempt.errors === 0 ? t('ui.progress.gradeGreat') : attempt.errors <= 2 ? t('ui.progress.gradeGood') : t('ui.progress.gradeRetake')}
                        </div>
                        <p className="text-[10px] text-foreground font-bold">{attemptScore} {t('ui.progress.pts')}</p>
                      </div>
                    </Card>
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <BottomNavigation />
    </MobileContainer>
  );
}
